import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// TEST_MARKER_20260529_0905
window.TEST_MARKER = '20260529_0905';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  return fetch(url, { ...options, headers: { ...(options.headers || {}), 'x-token': getToken() } });
};

import StatusCardPanel from './MonitorComponents/StatusCardPanel';
import AiAnalysisCard from './MonitorComponents/AiAnalysisCard';

const BEIRAN_PROJECT_ID = '1826186302246187010';
const LIGHT_COLORS = {
  green: { border: '#52c41a', bg: '#f6ffed', text: '#389e0d', emoji: '🟢', label: '快' },
  yellow: { border: '#faad14', bg: '#fffbe6', text: '#d48806', emoji: '🟡', label: '一般' },
  red: { border: '#ff4d4f', bg: '#fff2f0', text: '#cf1322', emoji: '🔴', label: '慢' },
  alert: { border: '#ff4d4f', bg: '#fff1f0', text: '#a8071a', emoji: '⚠️', label: '异常' },
  gray: { border: '#d9d9d9', bg: '#fafafa', text: '#999', emoji: '⚪', label: '无数据' }
};
const DEFAULT_NOTES = {
  overall: `📋 整体说明(可编辑)
信号灯判定逻辑(按各场景独立基准值,响应时间单位:秒s):
🟢 快:响应时间 ≤ 基准值 × 1.2
🟡 一般:基准值 × 1.2 < 响应时间 ≤ 基准值 × 1.5
🔴 慢:基准值 × 1.5 < 响应时间 ≤ 基准值 × 2
⚠️ 异常:响应时间 > 基准值 × 2
⚪ 无数据:该时点无有效监控记录`,
  statusCard: '今日各场景响应时间卡片,快/慢状态按各场景独立基准值判定。',
  comparison: '迁移前后对比表,展示各场景硬件迁移前后的平均响应时长及提升率。',
  weeklyChart: '近7天趋势图,柱状图=各场景响应时间(左轴);折线图=在线人数(右轴)。',
  trafficGrid: '近7天系统响应速度表格,行=场景,列=日期×时点,颜色=响应状态。'
};

export default function MonitorReport() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  // 北燃项目用传统布局,其他走动态布局
  if (projectId === BEIRAN_PROJECT_ID) {
    return <MonitorReportBeiran projectId={projectId} navigate={navigate} />;
  }
  return <MonitorReportFlex projectId={projectId} navigate={navigate} />;
}

// ===== 北燃传统布局版 =====
function MonitorReportBeiran({ projectId, navigate }) {
  const [loading, setLoading] = useState(true);
  const [todayStatus, setTodayStatus] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [config, setConfig] = useState({ scenarios: [], timePoints: [] });
  const weeklyChartRef = useRef(null);
  const weeklyChartInst = useRef(null);
  const onlineBarRef = useRef(null);

  useEffect(() => {
    if (!onlineBarRef.current || !weeklyData || !weeklyData.dates) return;
    const container = onlineBarRef.current;
    let chart = null;
    const timer = setTimeout(() => {
      try {
        chart = window.echarts.init(container);
        var wd = weeklyData || {};
        var dates = wd.dates || [];
        var timePoints = wd.timePoints || ['09:00','11:00','14:00','16:00'];
        var onlineByDate = wd.onlineByDate || {};
        var colors = ['#3B82F6','#10B981','#F59E0B','#EF4444'];
        var series = timePoints.map(function(tp, i) {
          return {
            name: tp + ' 在线',
            type: 'bar', barGap: '10%',
            data: dates.map(d => (onlineByDate[d]&&onlineByDate[d][tp]!=null)?onlineByDate[d][tp]:0),
            itemStyle: { color: colors[i%4], borderRadius: [4,4,0,0] },
            label: { show: true, position: 'top', fontSize: 9, padding: [0,0,4,0] }
          };
        });
        chart.setOption({
          tooltip: { trigger: 'axis' },
          grid: { top: 20, bottom: 30, left: 50, right: 20 },
          xAxis: { type: 'category', data: dates.map(d=>d.substring(5)), axisLabel: { fontSize: 10 } },
          yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
          series: series
        });
      } catch(e) { console.error(e); }
    }, 100);
    const ro = new ResizeObserver(() => {
      try { if (chart) chart.resize(); } catch(e) {}
    });
    ro.observe(container);
    return () => { clearTimeout(timer); ro.disconnect(); try { if (chart) chart.dispose(); } catch(e) {} };
  }, [weeklyData]);
  const [reportNotes, setReportNotes] = useState(DEFAULT_NOTES);
  const [editingNoteKey, setEditingNoteKey] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => { if (projectId) { loadAllData(); loadNotes(); } }, [projectId, selectedDate]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const params = selectedDate ? `?date=${selectedDate}` : '';
      const [statusRes, compRes, weekRes, gridRes, cfgRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/monitor/scenario-status/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/comparison/${projectId}`),
        fetchWithAuth(`${API_URL}/api/monitor/weekly/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/traffic-grid/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/config/${projectId}`)
      ]);
      const statusJson = await statusRes.json();
      const compJson = await compRes.json();
      const weekJson = await weekRes.json();
      const gridJson = await gridRes.json();
      const cfgJson = await cfgRes.json();
      if (statusJson.success) setTodayStatus(statusJson);
      if (compJson.success) setComparison(compJson);
      if (weekJson.success) setWeeklyData(weekJson);
      if (gridJson.success) setGridData(gridJson);
      if (cfgJson.success) setConfig(cfgJson.config || { scenarios: [], timePoints: [] });
    } catch (e) { console.error('[MonitorReport] 加载失败:', e); }
    setLoading(false);
  };

  const loadNotes = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/report-notes/${projectId}`);
      const data = await res.json();
      if (data.success && data.notes) {
        setReportNotes({ ...DEFAULT_NOTES, ...data.notes });
      }
    } catch (e) { console.error(e); }
  };

  const saveNote = async (key, text) => {
    const updated = { ...reportNotes, [key]: text };
    setReportNotes(updated);
    setEditingNoteKey(null);
    try {
      await fetchWithAuth(`${API_URL}/api/monitor/report-notes/${projectId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: updated })
      });
      await loadNotes();
    } catch (e) { console.error(e); }
  };

  const fmt = (v) => v != null ? Number(v).toFixed(1) : '-';

  // 近7天趋势图
  const initWeeklyChart = () => {
    if (!weeklyChartRef.current || !window.echarts || !weeklyData || !weeklyData.dates) return;
    const chart = window.echarts.init(weeklyChartRef.current);
    const colors = ['#3B82F6', '#6366F1', '#8B5CF6', '#A78BFA'];
    const { dates, onlineByDate, responseByScenario, timePoints } = weeklyData;
    const onlineData = dates.map(d => {
      const day = onlineByDate?.[d] || {};
      const vals = timePoints.map(tp => day[tp]).filter(v => v != null);
      return vals.length > 0 ? (vals.reduce((a,b) => a+b,0) / vals.length).toFixed(0) : 0;
    });
    const datasets = [];
    responseByScenario?.forEach((sc, idx) => {
      datasets.push({
        name: sc.name, type: 'bar',
        data: dates.map(d => sc.data?.[d] != null ? sc.data[d] : null),
        itemStyle: { color: colors[idx % colors.length] + '66', borderRadius: [3, 3, 0, 0] },
        barGap: '10%', yAxisIndex: 0
      });
    });
    datasets.push({
      name: '在线人数', type: 'line',
      data: onlineData,
      lineStyle: { color: '#1E40AF', width: 2 },
      itemStyle: { color: '#1E40AF' }, yAxisIndex: 1
    });
    chart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, itemWidth: 8, itemHeight: 8, textStyle: { fontSize: 10 } },
      grid: { top: 10, right: 20, bottom: 50, left: 50 },
      xAxis: { type: 'category', data: dates, axisLabel: { fontSize: 10 } },
      yAxis: [
        { type: 'value', name: '响应(s)', nameTextStyle: { fontSize: 10, padding: [0, 4, 0, 0] }, axisLabel: { fontSize: 10 } },
        { type: 'value', name: '在线', nameTextStyle: { fontSize: 10, padding: [0, 0, 0, 4] }, axisLabel: { fontSize: 10 }, splitLine: { show: false } }
      ],
      series: datasets
    });
  };

  useEffect(() => {
    if (weeklyChartRef.current && weeklyData) { const timer = setTimeout(initWeeklyChart, 100); return () => clearTimeout(timer); }
  }, [weeklyData]);

  if (loading) return <div style={{ padding: 40, fontSize: 14 }}>加载中...</div>;

  return (
    <div style={{ maxWidth: '100%', margin: '0', padding: '12px 24px', fontSize: '13px' }}>
      {/* 标题 + 日期选择 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
        <h1 style={{ fontSize: '18px', margin: 0 }}>📄 监控报告</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#666' }}>基准日期</label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }} />
          <button onClick={loadAllData} className="btn-primary" style={{ fontSize: '12px', padding: '4px 12px' }} disabled={loading}>🔄 刷新</button>
          <button onClick={() => navigate('/monitor')} className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}>← 返回监控</button>
        </div>
      </div>

      {/* 整体说明 - 可编辑 */}
      <div style={{ padding: '8px 12px', background: '#f0f5ff', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', lineHeight: '1.8', color: '#555' }}>
        {editingNoteKey !== 'overall' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ whiteSpace: 'pre-wrap', width: '100%' }}>{reportNotes.overall}</div>
            <button onClick={() => { setEditingNoteKey('overall'); setEditingNoteText(reportNotes.overall); }}
              style={{ fontSize: '11px', padding: '2px 8px', border: '1px solid #d9d9d9', borderRadius: '4px', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 8 }}>✏️</button>
          </div>
        )}
        {editingNoteKey === 'overall' && (
          <div>
            <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)}
              style={{ width: '100%', minHeight: 140, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12, lineHeight: 1.8 }} />
            <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
              <button onClick={() => saveNote('overall', editingNoteText)} className="btn-primary" style={{ fontSize: 12, padding: '4px 12px' }}>保存</button>
              <button onClick={() => setEditingNoteKey(null)} className="btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}>取消</button>
            </div>
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: '11px', color: '#666' }}>
          <strong>各场景基准响应时间(配置值):</strong>
          {config && config.scenarios && config.scenarios.map((sc, i) => (
            <span key={sc.id}>{i > 0 && '、'}{sc.name.trim()}({sc.baseline_ms||0}s)</span>
          ))}
        </div>
      </div>

      {/* 第1行:今日状态卡片 */}
      {todayStatus && todayStatus.scenarios && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {todayStatus.scenarios.map(sc => {
              const l = LIGHT_COLORS[sc.trafficLight] || LIGHT_COLORS.gray;
              // 同比显示
              const yoyDisplay = sc.yoyImprovementRate != null ? (
                <div style={{ 
                  marginTop: '6px', 
                  fontSize: '11px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  color: sc.yoyIsBetter ? '#059669' : '#dc2626'
                }}>
                  <span style={{ color: '#9ca3af' }}>同比</span>
                  <span style={{ fontWeight: 500 }}>
                    {sc.yoyIsBetter ? '↑' : '↓'} {Math.abs(sc.yoyImprovementRate)}%
                  </span>
                </div>
              ) : null;
              
              return (
                <div key={sc.id} style={{ borderLeft: `3px solid ${l.border}`, background: '#fff', padding: '8px 10px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ background: l.bg, color: l.text, width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', fontSize: '12px' }}>{l.emoji}</span>
                    <span style={{ fontSize: '12px', color: '#555' }}>{sc.name}</span>
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 600, color: '#333' }}>{fmt(sc.avgResponseSec)}<span style={{ fontSize: '10px', color: '#999' }}>s</span></div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>🧑‍💻 {Number(sc.avgOnline).toFixed(0)}<span style={{ fontSize: '10px', color: '#999' }}> 在线</span></div>
                  {yoyDisplay}
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: '11px', color: '#888', marginTop: '6px', marginBottom: 0 }}>📊 今日状态为4个监控时点(9,11,14,16),各个场景的平均在线人数和该场景平均响应时长。同比为与去年同期响应时间对比，↑表示效率提升（响应时间减少）。</p>
        </div>
      )}

      {/* 第2行:横版 - 左边对比表,右边趋势图 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        {/* 左:迁移前后对比 */}
        {comparison && comparison.scenarios && (
          <div style={{ background: '#fff', borderRadius: '6px', padding: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '13px', margin: '0 0 8px 0' }}>🔄 迁移前后对比</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    <th rowSpan={2} style={{ padding: '4px 6px', textAlign: 'left', borderRight: '2px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle' }}>场景</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>迁移前(s)</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>迁移后(s)</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>提升率</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.scenarios.map(sc => (
                    <tr key={sc.id}>
                      <td style={{ padding: '4px 6px', borderBottom: '1px solid #f0f0f0' }}>{sc.name}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{fmt(sc.beforeAvg)}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0' }}>{fmt(sc.afterAvg)}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', color: sc.isBetter ? '#52c41a' : '#ff4d4f' }}>
                        {sc.improvementRate != null ? `${sc.isBetter ? '↑' : '↓'} ${Math.abs(sc.improvementRate)}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '11px', color: '#888', marginTop: '6px', marginBottom: 0 }}>📊 取迁移日期前后各30天的平均响应时间对比,展示各场景性能变化。不足30天的,取实际监控天数的平均值。</p>
          </div>
        )}

        {/* 右:近7天趋势图 */}
        {weeklyData && (
          <div style={{ background: '#fff', borderRadius: '6px', padding: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '13px', margin: '0 0 8px 0' }}>📈 近7天趋势</h3>
            <div ref={weeklyChartRef} style={{ height: '120px' }}></div>
            <p style={{ fontSize: '11px', color: '#888', marginTop: '6px', lineHeight: '1.5' }}>📊 柱状图=各场景响应时间(左轴);折线图=在线人数(右轴)。人数取近7天每天4个统计时点(9,11,14,16)的平均值,响应时间为各场景实际数据。</p>
          </div>
        )}
      </div>

      {/* 第3行:红绿灯表格 */}
      {gridData && gridData.scenarios && (
        <div style={{ background: '#fff', borderRadius: '6px', padding: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '12px', margin: '0 0 6px 0' }}>📊 近7天系统响应速度</h3>
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              const cellMap = {};
              (gridData.grid || []).forEach(g => { cellMap[g.date + '_' + g.timePoint + '_' + g.scenarioId] = g; });
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ padding: '2px 4px', textAlign: 'left', borderRight: '2px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle', fontWeight: 600, background: '#fafafa', fontSize: '10px' }}>场景</th>
                      {gridData.dates?.map(d => (
                        <th key={d} colSpan={gridData.timePoints?.length || 4} style={{ padding: '2px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', background: '#fafafa', fontSize: '10px' }}>
                          {d.substring(5)}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {gridData.dates?.map((d, di) => gridData.timePoints?.map((tp, ti) => (
                        <th key={`${d}_${tp}`} style={{ padding: '1px 2px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', borderRight: (ti === gridData.timePoints.length - 1 && di < gridData.dates.length - 1) ? '1px solid #e5e7eb' : 'none', background: '#fafafa', fontSize: '9px', fontWeight: 400 }}>
                          {tp.substring(0, 2)}
                        </th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {gridData.scenarios.map(sc => (
                      <tr key={sc.id}>
                        <td style={{ padding: '2px 4px', borderRight: '2px solid #e5e7eb', borderBottom: '1px solid #f0f0f0', fontWeight: 600, fontSize: '9px' }}>{sc.name}</td>
                        {gridData.dates?.map((d, di) => gridData.timePoints?.map((tp, ti) => {
                          const key = d + '_' + tp + '_' + sc.id;
                          const g = cellMap[key];
                          if (!g) return <td key={key} style={{ padding: '2px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: (ti === gridData.timePoints.length - 1 && di < gridData.dates.length - 1) ? '1px solid #e5e7eb' : 'none', fontSize: '9px', color: '#ccc' }}>-</td>;
                          const l = LIGHT_COLORS[g.light] || LIGHT_COLORS.gray;
                          return (
                            <td key={key} style={{ padding: '2px', textAlign: 'center', borderBottom: '1px solid #f0f0f0', borderRight: (ti === gridData.timePoints.length - 1 && di < gridData.dates.length - 1) ? '1px solid #e5e7eb' : 'none', background: l.bg, color: l.text, fontSize: '9px', lineHeight: '1.4' }}
                              title={`${sc.name} ${d} ${tp}\n响应: ${g.responseTime}s\n状态: ${l.label}`}>
                              {l.emoji} {g.responseTime}s
                            </td>
                          );
                        }))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
          <p style={{ fontSize: '10px', color: '#888', marginTop: '4px', marginBottom: 0, lineHeight: '1.4' }}>📊 近7天x{gridData.timePoints?.length || 4}时点x各场景的响应时间。格内:emoji=状态,数字=响应时间(s)。颜色:{Object.values(LIGHT_COLORS).map(l => l.emoji + l.label).join('/')}。</p>
        </div>
      )}
      {/* 第4行：近7天在线人数 + 智能分析 并列 */}
      {weeklyData && (
        <div style={{ display: 'flex', gap: 12, marginBottom: '12px' }}>
          {/* 左列：近7天在线人数 */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '6px', padding: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: '13px', margin: '0 0 8px 0' }}>📊 近7天在线人数</h3>
            <div ref={onlineBarRef} style={{ flex: 1, minHeight: '150px' }}></div>
            <p style={{ fontSize: '11px', color: '#888', marginTop: '6px', lineHeight: '1.5' }}>📊 柱状图=各时点(9,11,14,16)4色区分，每天4个统计时点的平均在线人数。图例：<span style={{ color: '#3B82F6' }}>09:00</span> <span style={{ color: '#10B981' }}>11:00</span> <span style={{ color: '#F59E0B' }}>14:00</span> <span style={{ color: '#EF4444' }}>16:00</span></p>
          </div>
          {/* 右列：智能分析 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <AiAnalysisCard projectId={projectId} fontSize={11} />
          </div>
        </div>
      )}
    </div>
  );
}



// ===== 其他项目动态布局版 =====
function MonitorReportFlex({ projectId, navigate }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [data, setData] = useState({});
  const [selectedDate, setSelectedDate] = useState('');
  const [reportNotes, setReportNotes] = useState(DEFAULT_NOTES);
  const [editingNoteKey, setEditingNoteKey] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => { loadConfig(); loadData(); loadNotes(); }, [projectId, selectedDate]);

  const loadNotes = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/report-notes/${projectId}`);
      const data = await res.json();
      if (data.success && data.notes) {
        setReportNotes(prev => ({ ...DEFAULT_NOTES, ...data.notes }));
      }
    } catch (e) { console.error(e); }
  };

  const loadConfig = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${projectId}`);
      const json = await res.json();
      if (json.success) setConfig(json.config);
    } catch (e) { console.error(e); }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedDate ? `?date=${selectedDate}` : '';
      const [r1, r2, r3, r4, r5, r6, r7, r8] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/monitor/scenario-status/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/comparison/${projectId}`),
        fetchWithAuth(`${API_URL}/api/monitor/weekly/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/traffic-grid/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/response-distribution/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/yearly-monthly-avg/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/monthly-avg-online/${projectId}${params}`),
        fetchWithAuth(`${API_URL}/api/monitor/monthly-timepoint-avg/${projectId}${params}`)
      ]);
      const [d1, d2, d3, d4, d5, d6, d7, d8] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json(), r5.json(), r6.json(), r7.json(), r8.json()]);
      if (d1.success) setData(prev => ({ ...prev, todayStatus: d1 }));
      if (d2.success) setData(prev => ({ ...prev, comparison: d2 }));
      if (d3.success) setData(prev => ({ ...prev, weeklyData: d3 }));
      if (d4.success) setData(prev => ({ ...prev, gridData: d4 }));
      if (d5.success) setData(prev => ({ ...prev, responseDist: d5 }));
      if (d6.success) setData(prev => ({ ...prev, monthlyAvg: d6 }));
      if (d7.success) setData(prev => ({ ...prev, monthlyAvgOnline: d7 }));
      if (d8.success) setData(prev => ({ ...prev, monthlyTpAvg: d8 }));
    } catch (e) { console.error('[MonitorReport] 加载失败:', e); }
    setLoading(false);
  };

  const saveNote = async (key, text) => {
    const updated = { ...reportNotes, [key]: text };
    setReportNotes(updated);
    setEditingNoteKey(null);
    try {
      await fetchWithAuth(`${API_URL}/api/monitor/report-notes/${projectId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes: updated })
      });
      await loadNotes();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ padding: 40, fontSize: 14 }}>加载中...</div>;

  return (
    <div style={{ maxWidth: 'calc(100vw - 234px)', margin: '0 auto', padding: '12px 24px', fontSize: '13px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
        <h1 style={{ fontSize: '18px', margin: 0 }}>📄 监控报告
          {config?.reportLayout && <span style={{ fontSize: '11px', marginLeft: 8, color: '#8B5CF6', background: '#f5f3ff', padding: '2px 8px', borderRadius: '10px' }}>🎨 布局模式</span>}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#666' }}>基准日期</label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }} />
          <button onClick={() => navigate('/monitor')} className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}>← 返回监控</button>
        </div>
      </div>

      {/* 整体说明编辑区 */}
      <div className="section-block" style={{ marginBottom: 12 }}>
        <div className="section-header">
          <h2 className="section-title">📋 整体说明</h2>
          {editingNoteKey !== 'overall' && (
            <button onClick={() => { setEditingNoteKey('overall'); setEditingNoteText(reportNotes.overall); }}
              style={{ fontSize: '11px', padding: '2px 8px', border: '1px solid #d9d9d9', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>✏️ 编辑</button>
          )}
        </div>
        {editingNoteKey === 'overall' ? (
          <div>
            <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)}
              style={{ width: '100%', minHeight: 80, padding: 8, border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 12, lineHeight: 1.8 }} />
            <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
              <button onClick={() => saveNote('overall', editingNoteText)} className="btn-primary" style={{ fontSize: 12, padding: '4px 12px' }}>保存</button>
              <button onClick={() => setEditingNoteKey(null)} className="btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}>取消</button>
            </div>
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{reportNotes.overall}</div>
        )}
      </div>

      {/* 使用 reportLayout,没有则用默认全显示 */}
      {!config?.reportLayout ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>未配置报告布局,请先到监控主页配置布局。</div>
      ) : (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {config.reportLayout.map((item, idx) => {
            const { type, config: itemConfig } = item;
            const height = (itemConfig?._heightScale || 1) * (itemConfig?.height || 200);
            switch (type) {
              case 'statusCard': return <StatusCardPanel key={idx} todayStatus={data.todayStatus} LIGHT_COLORS={LIGHT_COLORS} fmt={fmt} />;
              case 'comparisonTable': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>🔄 迁移前后对比</h3>
                <p style={{ fontSize: 11, color: '#888' }}>{reportNotes.comparison}</p>
              </div>;
              case 'weeklyChart': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>📈 近7天趋势</h3>
                <div style={{ height: height + 'px' }} ref={el => { if(el) window.echarts?.init(el); }}></div>
                <p style={{ fontSize: 11, color: '#888' }}>{reportNotes.weeklyChart}</p>
              </div>;
              case 'trafficGrid': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 12, margin: '0 0 6px 0' }}>📊 近7天系统响应速度</h3>
                <p style={{ fontSize: 10, color: '#888' }}>{reportNotes.trafficGrid}</p>
              </div>;
              case 'onlineBar': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>📊 近7日在线人数</h3>
                <div ref={el => { if(el) {
                  setTimeout(() => {
                    try {
                      var chart = window.echarts.init(el);
                      var wd = data.weeklyData || {};
                      var dates = wd.dates || [];
                      var timePoints = wd.timePoints || ['09:00','11:00','14:00','16:00'];
                      var onlineByDate = wd.onlineByDate || {};
                      var colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];
                      var series = timePoints.map(function(tp, i) {
                        return {
                          name: tp + ' 在线',
                          type: 'bar',
                          data: dates.map(d => (onlineByDate[d]&&onlineByDate[d][tp]!=null)?onlineByDate[d][tp]:0),
                          itemStyle: { color: colors[i%4], borderRadius: [4,4,0,0] },
                          barMaxWidth: 24, barGap: '15%',
                          label: { show: true, position: 'top', fontSize: 10 }
                        };
                      });
                      chart.setOption({
                        tooltip: { trigger: 'axis' },
                        legend: { bottom: 0, textStyle: { fontSize: 11 } },
                        grid: { top: 10, bottom: 40, left: 50, right: 20 },
                        xAxis: { type: 'category', data: dates.map(d=>d.substring(5)), axisLabel: { fontSize: 10 } },
                        yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
                        series: series
                      });
                    } catch(e) {}
                  }, 100);
                }}} style={{ height: height + 'px' }}></div>
                <p style={{ fontSize: 10, color: '#888' }}>{reportNotes.onlineBar || '各时点在线人数分布(9/11/14/16)'}</p>
              </div>;
              case 'pieChart': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>🥧 响应时间分布</h3>
                <div ref={el => { if(el) {
                  setTimeout(() => {
                    try {
                      var chart = window.echarts.init(el);
                      var d = (data.responseDist||{}).distribution || {};
                      var pdata = [
                        { value: d.green||0, name: '🟢 快', itemStyle: { color: '#52c41a' } },
                        { value: d.yellow||0, name: '🟡 一般', itemStyle: { color: '#faad14' } },
                        { value: d.red||0, name: '🔴 慢', itemStyle: { color: '#ff4d4f' } },
                        { value: d.alert||0, name: '⚠️ 异常', itemStyle: { color: '#722ed1' } }
                      ];
                      chart.setOption({
                        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                        legend: { bottom: 0, textStyle: { fontSize: 11 } },
                        series: [{ type: 'pie', radius: ['35%','65%'], center: ['50%','45%'], data: pdata,
                          label: { show: true, formatter: '{b}\n{d}%', fontSize: 11 } }]
                      });
                    } catch(e) {}
                  }, 100);
                }}} style={{ height: height + 'px' }}></div>
                <p style={{ fontSize: 10, color: '#888' }}>{reportNotes.pieChart || '近7天各场景响应时间占比'}</p>
              </div>;
              case 'monthlyAvg': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>📈 月度平均响应时间</h3>
                <div ref={el => { if(el) {
                  setTimeout(() => {
                    try {
                      var chart = window.echarts.init(el);
                      var mad = data.monthlyAvg || {};
                      var months = mad.months || [];
                      var scenarios = mad.scenarios || [];
                      var lineColors = ['#3B82F6','#6366F1','#8B5CF6','#EC4899','#F59E0B','#10B981'];
                      var series = scenarios.map(function(sc,i){ return {
                        name: sc.name, type: 'line', smooth: true, symbol: 'circle', symbolSize: 6,
                        data: (sc.data||[]).map(v => v!=null?Number(v):null),
                        lineStyle: { width: 2, color: lineColors[i%6] }, itemStyle: { color: lineColors[i%6] },
                        label: { show: true, position: 'top', fontSize: 10, formatter: function(p){ return p.value!=null?Number(p.value).toFixed(1):''; } }
                      }; });
                      chart.setOption({
                        tooltip: { trigger: 'axis' },
                        legend: { bottom: 0, textStyle: { fontSize: 11 }, type: 'scroll' },
                        grid: { left: 50, right: 20, top: 10, bottom: 50 },
                        xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
                        yAxis: { type: 'value', name: '响应时间(s)', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
                        series: series
                      });
                    } catch(e) {}
                  }, 100);
                }}} style={{ height: height + 'px' }}></div>
                <p style={{ fontSize: 10, color: '#888' }}>{reportNotes.monthlyAvg || '各场景月度平均响应时长趋势'}</p>
              </div>;
              case 'monthlyAvgBar': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>📊 全年各月日均在线</h3>
                <div ref={el => { if(el) {
                  setTimeout(() => {
                    try {
                      var chart = window.echarts.init(el);
                      var mao = data.monthlyAvgOnline || {};
                      var months = mao.months || [];
                      var avgData = mao.avgOnline || [];
                      var avgVal = avgData.length>0 ? Math.round(avgData.reduce((a,b)=>a+b,0)/avgData.length) : 0;
                      chart.setOption({
                        tooltip: { trigger: 'axis' },
                        grid: { left: 50, right: 20, top: 10, bottom: 50 },
                        xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
                        yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
                        series: [
                          { name: '日均在线', type: 'bar', data: avgData, itemStyle: { color: '#3B82F6', borderRadius: [4,4,0,0] }, barMaxWidth: 40,
                            markLine: { silent: true, lineStyle: { color: '#EF4444', type: 'dashed', width: 2 },
                              label: { formatter: avgVal+'人', fontSize: 10, color: '#EF4444' }, data: [{ yAxis: avgVal }] }
                          },
                          { name: '趋势', type: 'line', data: avgData, smooth: true, symbol: 'circle', symbolSize: 6,
                            lineStyle: { color: '#8B5CF6', width: 2 }, itemStyle: { color: '#8B5CF6' },
                            label: { show: true, position: 'top', fontSize: 10, color: '#8B5CF6' }
                          }
                        ]
                      });
                    } catch(e) {}
                  }, 100);
                }}} style={{ height: height + 'px' }}></div>
                <p style={{ fontSize: 10, color: '#888' }}>每月日均在线人数+平均线+趋势线</p>
              </div>;
              case 'monthlyGroupBar': return <div key={idx} style={{ background: '#fff', borderRadius: 6, padding: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, margin: '0 0 8px 0' }}>📊 全年各时段平均在线</h3>
                <div ref={el => { if(el) {
                  setTimeout(() => {
                    try {
                      var chart = window.echarts.init(el);
                      var mta = data.monthlyTpAvg || {};
                      var months = mta.months || [];
                      var timePoints = mta.timePoints || ['09:00','11:00','14:00','16:00'];
                      var tpData = mta.data || {};
                      var colors = ['#3B82F6','#10B981','#F59E0B','#EF4444'];
                      var series = timePoints.map(function(tp,i){ return {
                        name: tp+' 在线', type: 'bar', barGap: '10%',
                        data: (tpData[tp]||[]),
                        itemStyle: { color: colors[i%4], borderRadius: [3,3,0,0] },
                        label: { show: true, position: 'top', fontSize: 9 }
                      }; });
                      chart.setOption({
                        tooltip: { trigger: 'axis' },
                        legend: { bottom: 0, textStyle: { fontSize: 11 } },
                        grid: { left: 50, right: 20, top: 10, bottom: 50 },
                        xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
                        yAxis: { type: 'value', name: '在线人数', nameTextStyle: { fontSize: 10 }, axisLabel: { fontSize: 10 } },
                        series: series
                      });
                    } catch(e) {}
                  }, 100);
                }}} style={{ height: height + 'px' }}></div>
                <p style={{ fontSize: 10, color: '#888' }}>各月分时点分组柱状图</p>
              </div>;
                            case 'aiAnalysis': return <div key={idx} style={{ marginBottom: 12 }}>
                  <AiAnalysisCard projectId={projectId} date={selectedDate} data={{ todayStatus: data.todayStatus, weeklyData: data.weeklyData, responseDist: data.responseDist }} />
                </div>;
              default: return null;
            }
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 8 }}>
          <button onClick={loadData} className="btn-primary" style={{ fontSize: '12px', padding: '6px 16px' }} disabled={loading}>🔄 刷新</button>
        </div>
      </>
      )}
    </div>
  );
}
